import { Cached } from '@mui/icons-material';
import { Button, Tooltip } from '@mui/material';
import Box from '@mui/material/Box';
import { useFormikContext } from 'formik';
import type { ReactElement } from 'react';

import type { QuoteFormikType } from '@core/formik/quote';
import { SetFormikValue } from '@core/utils/formikHelpers';
import { Input } from '@web/components/Input/Input';
import { useSlabQuery } from '@web/hooks/useSlabQuery';

type NotesSectionProps = {
  label?: string;
};

export const NotesSection = ({ label }: NotesSectionProps = {}): ReactElement => {
  const formikBag = useFormikContext<QuoteFormikType>();
  const hasProject = formikBag.values.project.id !== null;

  const {
    data: project,
    isLoading: isLoadingProject,
    isError: isErrorProject,
  } = useSlabQuery(
    'GET project by ID',
    {
      pathParams: {
        id: formikBag.values.project.id ?? '',
      },
    },
    {
      enabled: hasProject,
    },
  );

  // Only the reload action depends on the project request, so a slow or failed
  // fetch must not take the notes field down with it.
  const canReload = hasProject && !isLoadingProject && !isErrorProject;
  const reloadTooltip = ((): string => {
    if (isLoadingProject) {
      return 'Loading the project...';
    }
    if (isErrorProject) {
      return 'Could not load the project. Try again later.';
    }
    return 'Clears the current notes and reloads from project';
  })();

  return (
    <>
      {hasProject && (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'end',
          }}
        >
          <Tooltip title={reloadTooltip} placement='top-start'>
            {/* A disabled MUI Button emits no pointer events, so the tooltip needs a live wrapper. */}
            <span>
              <Button
                disabled={!canReload}
                onClick={(): void => {
                  SetFormikValue(formikBag, 'notes', project?.notes ?? null);
                }}
              >
                <Cached />
              </Button>
            </span>
          </Tooltip>
        </Box>
      )}
      <Box>
        <Input type='textarea' name='notes' label={label} />
      </Box>
    </>
  );
};
